import { SettingsPermissions } from "@redotech/account-common/permissions";
import { SIGNATURE_MARKER, stripSignature } from "@redotech/email/signature";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import InfoSvg from "@redotech/redo-icons/info-circle.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { ClientUploader } from "@redotech/redo-merchant-app-common/client/upload";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { AutoReplyState } from "@redotech/redo-return-ui/flows/rules/auto-reply";
import { RuleTypeDetailsProps } from "@redotech/redo-return-ui/flows/rules/multiple-actions";
import { Attachment } from "@redotech/redo-support-common/create-conversation-body";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { LabeledInput, LabelPosition } from "@redotech/redo-web/labeled-input";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import Quill from "quill";
import { memo, useContext, useEffect, useRef, useState } from "react";
import { StepRichTextEditor } from "../chat-flow/step-rich-text";
import * as autoReplyCss from "./auto-reply.module.css";

export const AutoReplyDetails = memo(function AutoReplyDetails({
  state,
  setState,
  disabled,
}: RuleTypeDetailsProps<AutoReplyState>) {
  const { t } = useTranslation();
  const team = useContext(TeamContext);
  const [showPlaintext, setShowPlaintext] = useState(false);
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );
  const canEditSupportSettings = useHasPermission(
    SettingsPermissions.SET_SUPPORT_SETTINGS,
  );
  const canEditSettings = canEditReturnsSettings || canEditSupportSettings;

  const quillRef = useRef<Quill | undefined>(undefined);

  const handleMessage = useHandler(() => {
    if (!quillRef.current) {
      return;
    }
    let textContent = quillRef.current.getText();
    let htmlContent: string | undefined = quillRef.current.getSemanticHTML();
    if (textContent === "\n") {
      textContent = "";
    }
    if (htmlContent === "<p></p>") {
      htmlContent = "";
    }

    if (
      // Quill converts img tags to base 64, which won't match the url-format
      // img tags we have stored, so just check that img tags exist in the same
      // location, not what they contain
      neuterImgTags(htmlContent) !== neuterImgTags(state.htmlContent) ||
      textContent !== state.textContent
    ) {
      setState((state) => ({ ...state, textContent, htmlContent }));
    }
  });

  const handleAttachmentUpload = (attachment: Attachment) => {
    setState((state) => ({
      ...state,
      attachments: [...(state.attachments || []), attachment],
    }));
  };

  const handleAttachmentRemoval = (url: string) => {
    setState((state) => ({
      ...state,
      attachments: (state.attachments || []).filter(
        (attachment: Attachment) => attachment.url !== url,
      ),
    }));
  };

  useEffect(() => {
    if (quillRef.current && team?.settings?.support?.emailSignature) {
      // We use unicode character \u200B (zero-width space) to mark the start of the signature.
      const signature = `<br><br><p>&#x200B;--</p>${team?.settings?.support?.emailSignature}`;
      const quill: Quill = quillRef.current;
      const signatureIndex = quill.getText().indexOf(SIGNATURE_MARKER);
      if (signatureIndex === -1) {
        quill.clipboard.dangerouslyPasteHTML(quill.getLength() - 1, signature);
      }
    }
  }, [team]);

  return (
    <ClientUploader>
      <LabeledInput
        label={
          <div className={autoReplyCss.labelContainer}>
            <p>{t("Message to customer")}</p>
            <LabeledInput
              label={
                <div className={autoReplyCss.label}>
                  <Tooltip
                    placement="bottom-start"
                    title={t(
                      "The formatted version of the message will be used when replying to email messages. When responding to other message types such as {{integrationName}} or SMS, the plaintext message will be sent.",
                      { integrationName: "Facebook" },
                    )}
                  >
                    <div className={autoReplyCss.infoIcon}>
                      <InfoSvg />
                    </div>
                  </Tooltip>
                  {t("Show plaintext")}
                </div>
              }
              position={LabelPosition.LEFT}
            >
              <RedoToggle
                setValue={setShowPlaintext}
                size="sm"
                value={showPlaintext}
              />
            </LabeledInput>
          </div>
        }
      >
        {showPlaintext && (
          <p className={autoReplyCss.readonlyText}>
            {stripSignature(state.textContent)}
          </p>
        )}
        <div
          className={
            showPlaintext ? autoReplyCss.hideEditor : autoReplyCss.showEditor
          }
        >
          <StepRichTextEditor
            attachments={state.attachments}
            enableAttachments
            htmlValue={state.htmlContent}
            onAttachmentRemoval={handleAttachmentRemoval}
            onAttachmentUpload={handleAttachmentUpload}
            onTextChange={handleMessage}
            quillRef={quillRef}
            readOnly={!canEditSettings}
            textValue={state.textContent}
          />
        </div>
      </LabeledInput>
    </ClientUploader>
  );
});

const neuterImgTags = (htmlContent: string) => {
  const imgRegex = /<img.*?>/g;
  return htmlContent.replace(imgRegex, "<img />");
};
